<!--
  ContactsInput.vue

  The inline editor for a single-pick `contact` reference field — a schema field
  like `email` (e.g. the primary email). The field is schema-qualified: its
  `record_contacts` row carries `schema_key` (the field key) + `schema_id`, and the
  read model resolves the chip off that row, not `data[key]`. Also the read-only
  chip renderer for the aggregate `contacts` relation field (`isArray`, multi-chip)
  in read-only form context — same chips, the add-field just never shows. A
  `record_contacts` row holds a raw value, so
  the "add" path is typing a value (not search-and-link). As you type, the
  autocomplete lists this record's matching contacts (pick one to reference) and
  injects an "Add «value»" row so a new value is always creatable; both commit
  through the normal select path, so Enter works. Built on AutocompleteInput
  (like ReferenceInput) to share the chip + search chrome,
  including the focus ring on the wrapping `.chip-input`.

  Everything persists through the `record_contacts` rows and emits `changed` so
  the parent refetches — nothing writes `data[key]`. Creating stamps
  `schema_key`/`schema_id` on the new row; picking an existing contact stamps them
  onto that row (qualifying it to this field); removing clears them, unlinking the
  designation — the row survives as an unqualified contact, it is NOT deleted.

  Contact type — where the new row's `contact_type` comes from (see
  `fieldSubtype`):
    - a typed field (`email`/`phone`/`url`/`address`) fixes it; no picker.
    - a generic `contact` field shows a type picker for the next value.
  Per-type normalize lives on the FieldType in types/reference.ts (`parse`,
  normalize-or-null — parsing is validation), read here via `parseValue`.

  Chips always render off `field.value(item)` — the field's `schema_key`-scoped
  `record_contacts` row(s) — in both record and listing context. `modelValue` is
  the field's `rawValue` (those rows' ids), matching ReferenceInput. Used as the
  `form` slot; the grid display renders
  ContactElement chips through ReferenceCell (via the `contact`
  REFERENCE_REGISTRY entry). Editing the record's full contact list lives in
  ContactsBlock, not here.
-->
<template>
  <div class="input chip-input contacts-input" :class="`is-${size}`">
    <ContactElement
      v-for="contact in contacts"
      :key="contact.id ?? contact.value"
      :value="{
        contact_type: contact.contact_type,
        value: contact.value,
        label: contact.label ?? undefined,
      }"
      :size="size"
      :readonly="readonly"
      @remove="removeContact(contact.id)"
    />

    <template v-if="!readonly">
      <!-- Generic `contact` field: pick the contact type the next value
           becomes. Subtype fields (email/phone/url/address) fix the type, so
           no picker — and chips carry their own icon, so no leading badge. -->
      <select
        v-if="!fieldSubtype"
        v-model="genericType"
        class="contacts-type-select"
        aria-label="Contact type"
      >
        <option v-for="opt in typeOptions" :key="opt.value" :value="opt.value">
          {{ opt.label }}
        </option>
      </select>

      <!-- Single-pick: the input disappears once a contact is referenced. -->
      <AutocompleteInput
        v-if="contacts.length === 0"
        :results="searchResults"
        :placeholder="placeholder"
        :size="size"
        clear-on-select
        @search="handleSearch"
        @select="handleSelect"
        @remove-last="removeLast"
      >
        <template #suggestion="{ result }">
          <!-- Synthetic "create" row vs an existing contact match. The Add row
               is injected into the results so it shows even when partial
               matches exist (the #empty slot only renders at 0 results) and
               commits through the normal select path (so Enter works). -->
          <span v-if="isCreateRow(result)" class="contacts-create">
            <font-awesome-icon icon="plus" size="sm" />
            <span>Add “{{ createValue(result) }}”</span>
          </span>
          <ContactElement
            v-else
            :value="{ contact_type: activeType, value: result[1] }"
            :size="size"
            readonly
            fixed
          />
        </template>
      </AutocompleteInput>
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from "vue"
import { toast } from "vue-sonner"
import { type ContactType, type Field, FIELD_TYPES, parseValue } from "@auger/core/fields"
import AutocompleteInput from "@auger/ui/components/AutocompleteInput.vue"
import ContactElement from "@auger/ui/components/elements/ContactElement.vue"
import { searchContactsOnRecord } from "@/common/hasura/contacts"
import { client } from "@/init/apollo"
import { getAuger } from "@/init/auger"
import { errorMessage } from "@/utils/errors"

type ContactRow = {
  id?: number | string
  contact_type: string
  value: string
  label?: string | null
  is_primary?: boolean | null
}

const props = withDefaults(
  defineProps<{
    /** The field's raw stored value (its `rawValue`): one `record_contacts.id`
     *  for a single-pick contact field, or the array of junction-row ids for the
     *  aggregate `contacts` relation. Chips resolve off `field.value(item)`, not
     *  this — same raw-value contract as ReferenceInput. */
    modelValue?: number | string | (number | string)[] | null
    /** The row this picker belongs to. Its id (record context) or
     *  `item.record.id` (listing context) owns the created contact rows. */
    item?: Record<string, unknown> | null
    field?: Field
    readonly?: boolean
    size?: string
  }>(),
  {
    modelValue: null,
    item: null,
    field: undefined,
    readonly: false,
    size: "normal",
  }
)

const emit = defineEmits<{
  /** Emitted after any change (create / qualify / unqualify): all persist through
   *  the `record_contacts` rows here, so the parent must refetch for the updated
   *  `record.contacts` to resolve the chip. This input never writes `data[key]` —
   *  the schema_key row is the field's value. */
  changed: []
}>()

// CONTACTS ====================================================================

// Chips resolve off `field.value(item)` — the resolved `record_contacts`
// row(s) — in both record and listing context, mirroring ReferenceInput.
// `modelValue` is the raw stored id and isn't read here.
function isContactRow(v: unknown): v is ContactRow {
  return !!v && typeof v === "object" && "value" in (v as object)
}

const contacts = computed<ContactRow[]>(() => {
  const resolved = props.item && props.field ? props.field.value(props.item) : null
  if (resolved == null) return []
  return (Array.isArray(resolved) ? resolved : [resolved]).filter(isContactRow)
})

// CONTACT TYPE ================================================================

const CONTACT_TYPES: ContactType[] = ["email", "phone", "url", "location"]

// A concrete subtype field (email/phone/url/address) fixes the contact_type;
// the generic `contact` field doesn't, so it shows a picker (genericType).
const fieldSubtype = computed<ContactType | null>(() => {
  const t = props.field?.type
  return t && (CONTACT_TYPES as string[]).includes(t) ? (t as ContactType) : null
})

const typeOptions = CONTACT_TYPES.map((value) => ({
  value,
  label: FIELD_TYPES[value]?.label ?? value,
}))

const genericType = ref<ContactType>("email")

// The contact type driving the next value: the field's own subtype, or the
// picked one in generic mode.
const activeType = computed<ContactType>(() => fieldSubtype.value ?? genericType.value)
const activeLabel = computed(() => (FIELD_TYPES[activeType.value]?.label ?? "").toLowerCase())

// A synthetic field carrying the active contact type, so parseValue resolves
// that type's parse (normalize-or-null) even in generic mode.
const activeField = computed<Field>(() => ({ type: activeType.value }) as Field)

// PRESENTATION ================================================================

const placeholder = computed(() => `Add ${activeLabel.value}…`)

// OWNING RECORD ===============================================================

// `item` may be a listing wrapping the record under `record`; the contact
// belongs to the record itself (mirrors the junction hooks' resolution).
const recordId = computed<number | string | null>(() => {
  const item = props.item as { id?: number | string; record?: { id?: number | string } } | null
  return item?.record?.id ?? item?.id ?? null
})

// SEARCH ======================================================================

// AutocompleteInput results are [key, displayLabel] tuples. Existing matches key
// on the contact row id (so single-pick can emit it); the synthetic "create" row
// uses a sentinel key so select() can tell them apart. The Add row is injected
// into results (not the #empty slot) so it appears even alongside partial matches
// and commits via the normal select path — which also makes Enter create.
const CREATE_PREFIX = "__create__:"
const searchResults = ref<[string, string][]>([])
// Row by result key, so select() recovers the chosen contact (id + value).
const resultRows = new Map<string, ContactRow>()

function isCreateRow(result: [string, string]) {
  return typeof result[0] === "string" && result[0].startsWith(CREATE_PREFIX)
}
function createValue(result: [string, string]) {
  return result[0].slice(CREATE_PREFIX.length)
}

function handleSearch(query: string) {
  if (!query || query.length < 2 || recordId.value == null) {
    searchResults.value = []
    return
  }
  // Surface this record's matching contacts so the user can pick an existing
  // one instead of typing a near-duplicate (soft guidance, not a block), and
  // append an "Add «parsed»" row whenever the typed value is valid and isn't
  // already on the record — so a new value is always creatable, even when a
  // partial match exists.
  client
    .query({
      query: searchContactsOnRecord,
      variables: {
        recordId: Number(recordId.value),
        value: `%${query}%`,
        contactType: activeType.value,
      },
      fetchPolicy: "network-only",
    })
    .then(({ data }) => {
      const rows: ContactRow[] = data?.contacts ?? []
      resultRows.clear()
      const results = rows.map((r) => {
        const key = String(r.id)
        resultRows.set(key, r)
        return [key, r.value] as [string, string]
      })
      const parsed = parseValue(activeField.value, query) as string | null
      if (parsed && !contacts.value.some((c) => c.value === parsed)) {
        results.push([`${CREATE_PREFIX}${parsed}`, parsed])
      }
      searchResults.value = results
    })
    .catch((err: unknown) => {
      console.error("ContactsInput: contact suggestions failed", err)
      searchResults.value = []
    })
}

function resetSearch() {
  searchResults.value = []
  resultRows.clear()
  // The search box clears via AutocompleteInput's clear-on-select.
}

// CREATE / SELECT / REMOVE ====================================================

// The field's schema qualification — stamped onto a row so the read model
// resolves it to this field (create / pick), or cleared to unlink it (remove).
const qualifier = computed(() => {
  const field = props.field as { sourceKey?: string; schema_id?: number } | undefined
  return { schema_key: field?.sourceKey ?? null, schema_id: field?.schema_id ?? null }
})

// Create a new schema-qualified record_contacts row and return it (with its id),
// or null on failure. A single-pick contact field only reaches here; the
// aggregate `contacts` relation never shows the add-field.
async function createRow(value: string): Promise<ContactRow | null> {
  if (recordId.value == null) {
    console.error("ContactsInput: no owning record id; cannot add contact")
    return null
  }
  try {
    // The row is a value of this field's key, or of the aggregate `contacts`
    // field when the field carries none; core stamps schema_id from the field.
    const key = qualifier.value.schema_key ?? "contacts"
    const target = { contactType: activeType.value, value }
    const auger = getAuger(client)
    const { written } = await auger.record(Number(recordId.value)).contacts.add(key, target)
    const row = written.contacts[key]?.[0]
    if (!row) throw new Error(`no contact row came back for record ${recordId.value}`)
    return row
  } catch (err) {
    const error = errorMessage(err)
    console.error("ContactsInput: add contact failed", err)
    toast.error(`Failed to add contact: ${error}`)
    return null
  }
}

// Qualify (stamp schema_key/schema_id) or unlink (clear them) an existing row so
// the read model includes or drops it for this field. Returns success. Never
// writes `data[key]` — the row is the field's value.
async function qualifyRow(id: number, qualified: boolean): Promise<boolean> {
  try {
    const auger = getAuger(client)
    await auger.rows.qualify("contacts", id, (qualified ? qualifier.value : null) as never)
    return true
  } catch (err) {
    const error = errorMessage(err)
    console.error("ContactsInput: qualifyRow failed", err)
    toast.error(`Failed to update contact: ${error}`)
    return false
  }
}

// Selecting a suggestion: the synthetic Add row creates a new schema-qualified
// row; an existing match qualifies that row to this field. Both persist here and
// refetch via `changed` so the chip resolves — no `data[key]`.
async function handleSelect(result: [string, string]) {
  const ok = isCreateRow(result)
    ? !!(await createRow(createValue(result)))
    : await qualifyRow(Number(result[0]), true)
  if (ok) emit("changed")
  resetSearch()
}

// Removing the chip unlinks the designation by clearing the row's schema_key; the
// row survives as an unqualified contact (it may be referenced elsewhere) — it is
// NOT deleted.
async function removeContact(id: number | string | undefined) {
  if (id == null) return
  if (await qualifyRow(Number(id), false)) emit("changed")
}

// Backspace on the empty field removes the last chip, matching the junction
// input's remove-last affordance.
function removeLast() {
  const last = contacts.value[contacts.value.length - 1]
  if (last) removeContact(last.id)
}
</script>

<style>
.contacts-input {
  /* Layout (flex, gap, align-items: center, min-height) comes from the shared
     `.chip-input` rule — don't re-declare it. Only contacts-specific bits. */

  .autocomplete {
    flex: 1 1 auto;
    min-width: 120px;
  }

  .contacts-type-select {
    border: none;
    background: none;
    font-size: inherit;
    padding: 0 0.25rem 0 0;
    flex-shrink: 0;
  }
}

/* The Add row sits alongside ContactElement chip suggestions in the dropdown;
   match the chip's height and left indent so both row types align. */
.contacts-create {
  display: inline-flex;
  align-items: center;
  gap: 0.4em;
  height: 24px;
  padding: 0 0.35em;
  border: none;
  background: none;
  cursor: pointer;
  color: var(--auger-text);
  font: inherit;
}
</style>
